import { MONTH_SHORT, money, moneyShort } from '../lib/format';

interface Props {
  actual: (number | null)[];
  projected: (number | null)[];
  annualBudget: number;
}

const W = 520;
const H = 260;
const PAD = { top: 18, right: 14, bottom: 30, left: 52 };

/**
 * Cumulative spend against budget: solid line for months with actuals, dashed
 * for the straight-line projection, and a dashed budget ceiling.
 */
export function ProjectionChart({ actual, projected, annualBudget }: Props) {
  const values = [...actual, ...projected, annualBudget].filter(
    (v): v is number => v !== null && Number.isFinite(v),
  );
  const max = Math.max(annualBudget, ...values, 1) * 1.12;

  const plotWidth = W - PAD.left - PAD.right;
  const plotHeight = H - PAD.top - PAD.bottom;
  const x = (index: number) => PAD.left + (index * plotWidth) / 11;
  const y = (value: number) => PAD.top + plotHeight - (value / max) * plotHeight;

  const toPoints = (series: (number | null)[]) =>
    series
      .map((value, index) => (value === null ? null : `${x(index)},${y(value)}`))
      .filter((point): point is string => point !== null)
      .join(' ');

  const budgetY = y(annualBudget);
  const lastActualIndex = actual.reduce<number>(
    (last, value, index) => (value !== null ? index : last),
    -1,
  );
  const projectedEnd = projected[11];
  const overBudget = projectedEnd !== null && projectedEnd > annualBudget;

  const gridLines = [0.25, 0.5, 0.75, 1].map((fraction) => fraction * max);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label={`Cumulative expenses against an annual budget of ${money(annualBudget)}. Projected year end ${
        projectedEnd !== null ? money(projectedEnd) : 'unavailable'
      }.`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {gridLines.map((value) => (
        <g key={value}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y(value)}
            y2={y(value)}
            stroke="var(--bd)"
            strokeWidth="1"
          />
          <text x={PAD.left - 8} y={y(value) + 4} fontSize="10" textAnchor="end" fill="var(--mut)">
            {moneyShort(value)}
          </text>
        </g>
      ))}

      <line
        x1={PAD.left}
        x2={W - PAD.right}
        y1={budgetY}
        y2={budgetY}
        stroke="var(--warn)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
      />
      <text x={PAD.left + 4} y={budgetY - 6} fontSize="10" fill="var(--warn)">
        Annual budget {moneyShort(annualBudget)}
      </text>

      <polyline
        fill="none"
        stroke={overBudget ? 'var(--warn)' : 'var(--mid)'}
        strokeWidth="2"
        strokeDasharray="6 5"
        strokeLinecap="round"
        points={toPoints(projected)}
      />

      <polyline
        fill="none"
        stroke="var(--ac)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={toPoints(actual)}
      />

      {lastActualIndex >= 0 && actual[lastActualIndex] !== null && (
        <circle cx={x(lastActualIndex)} cy={y(actual[lastActualIndex])} r="4" fill="var(--ac)" />
      )}

      {projectedEnd !== null && (
        <circle
          cx={x(11)}
          cy={y(projectedEnd)}
          r="4"
          fill="none"
          stroke={overBudget ? 'var(--warn)' : 'var(--mid)'}
          strokeWidth="2"
        />
      )}

      {MONTH_SHORT.map((month, index) =>
        index % 2 === 0 ? (
          <text
            key={month}
            x={x(index)}
            y={H - 10}
            fontSize="10"
            textAnchor="middle"
            fill="var(--mut)"
          >
            {month}
          </text>
        ) : null,
      )}
    </svg>
  );
}
